import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getCurrentLocale, setLocale, translate, type Locale } from '@/i18n'
import { http } from '@/plugins/axios'
import { setVuetifyThemeMode, updateVuetifyThemeColors, type AppThemeName } from '@/plugins/vuetify'
import { apiEndpoints, buildApiPath } from '@/shared/constants/api'
import { localizeApiErrorMessage } from '@/shared/helpers/api-error'
import {
  readStoredPublicSystemSettings,
  writeStoredPublicSystemSettings,
} from '@/shared/helpers/system-setting-cache'
import type { ApiResponseEnvelope } from '@/shared/types/api.type'
import {
  createEmptySystemSettingForm,
  DEFAULT_SALE_SETTINGS,
  toSystemSettingForm,
  type BankAccount,
  type BankAccountForm,
  type BrandingAssetType,
  type BrandingPresignPayload,
  type BrandingPresignResponse,
  type SaleSettings,
  type SystemSetting,
  type SystemSettingForm,
  type SystemSettingPublic,
} from '@/shared/types/system-setting.type'
import { useSettingStore } from '@/stores/setting.store'

const LOCALE_PREFERENCE_KEY = 'bigsale.locale.preference'

function getErrorMessage(exception: unknown, fallbackKey = 'setting.system.errors.saveFailed') {
  if (exception instanceof Error && exception.message) {
    return localizeApiErrorMessage(exception.message)
  }

  return translate(fallbackKey)
}

function hasUserLocalePreference() {
  return Boolean(localStorage.getItem(LOCALE_PREFERENCE_KEY))
}

export function markUserLocalePreference(locale: Locale) {
  localStorage.setItem(LOCALE_PREFERENCE_KEY, locale)
}

function applyFavicon(url: string | null | undefined) {
  const href = url || '/favicon.ico'
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']")

  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }

  link.href = href
}

function applyDocumentTitle(displayName: string | null | undefined) {
  document.title = displayName?.trim() || 'BigSale'
}

function toPublicSetting(value: SystemSetting): SystemSettingPublic {
  return {
    display_name: value.display_name,
    logo_url: value.logo_url,
    favicon_url: value.favicon_url,
    primary_color: value.primary_color,
    default_locale: value.default_locale,
    date_format: value.date_format,
    time_format: value.time_format,
    sale_settings: {
      ...DEFAULT_SALE_SETTINGS,
      allow_negative_stock: Boolean(value.allow_negative_stock),
      allow_edit_price: Boolean(value.allow_edit_price),
      require_customer: Boolean(value.require_customer),
      allow_line_discount: value.allow_line_discount !== false,
      allow_invoice_discount: value.allow_invoice_discount !== false,
      auto_select_single_product: Boolean(value.auto_select_single_product),
      max_players_per_table: value.max_players_per_table,
      kitchen_enabled: value.kitchen_enabled !== false,
      location_management_enabled: Boolean(value.location_management_enabled),
      require_location_on_sale: Boolean(value.require_location_on_sale),
      allow_view_cancelled_invoices: value.allow_view_cancelled_invoices !== false,
      table_order_enabled: Boolean(value.table_order_enabled),
      sale_grid_enabled: Boolean(value.sale_grid_enabled),
      sale_return_enabled: true,
      sale_return_days: Number(value.sale_return_days ?? DEFAULT_SALE_SETTINGS.sale_return_days),
    },
  }
}

export const useSystemSettingStore = defineStore('system-setting', () => {
  const setting = ref<SystemSetting | null>(null)
  const publicSettings = ref<SystemSettingPublic | null>(readStoredPublicSystemSettings())
  const bankAccounts = ref<BankAccount[]>([])
  const form = ref<SystemSettingForm>(createEmptySystemSettingForm())
  const isLoading = ref(false)
  const isSaving = ref(false)
  const isUploadingBranding = ref(false)
  const isTestingSmtp = ref(false)
  const isSavingBankAccount = ref(false)
  const error = ref<string | null>(null)

  const saleSettings = computed<SaleSettings>(() => {
    const source =
      publicSettings.value?.sale_settings ||
      (setting.value
        ? {
            allow_negative_stock: Boolean(setting.value.allow_negative_stock),
            allow_edit_price: Boolean(setting.value.allow_edit_price),
            require_customer: Boolean(setting.value.require_customer),
            allow_line_discount: setting.value.allow_line_discount !== false,
            allow_invoice_discount: setting.value.allow_invoice_discount !== false,
            auto_select_single_product: Boolean(setting.value.auto_select_single_product),
            max_players_per_table: setting.value.max_players_per_table,
            kitchen_enabled: setting.value.kitchen_enabled !== false,
            location_management_enabled: Boolean(setting.value.location_management_enabled),
            require_location_on_sale: Boolean(setting.value.require_location_on_sale),
            allow_view_cancelled_invoices: setting.value.allow_view_cancelled_invoices !== false,
            table_order_enabled: Boolean(setting.value.table_order_enabled),
            sale_grid_enabled: Boolean(setting.value.sale_grid_enabled),
            sale_return_enabled: true,
            sale_return_days: Number(
              setting.value.sale_return_days ?? DEFAULT_SALE_SETTINGS.sale_return_days,
            ),
          }
        : null)

    return {
      ...DEFAULT_SALE_SETTINGS,
      ...source,
      max_players_per_table:
        Number(source?.max_players_per_table) > 0
          ? Number(source?.max_players_per_table)
          : DEFAULT_SALE_SETTINGS.max_players_per_table,
    }
  })

  const displayName = computed(
    () => publicSettings.value?.display_name || setting.value?.display_name || 'BigSale',
  )
  const logoUrl = computed(() => publicSettings.value?.logo_url || setting.value?.logo_url || null)
  const faviconUrl = computed(
    () => publicSettings.value?.favicon_url || setting.value?.favicon_url || null,
  )

  function applyToApp(source?: SystemSetting | SystemSettingPublic | null) {
    const payload = source || publicSettings.value || setting.value
    if (!payload) return

    const primary = payload.primary_color || '#2563EB'
    const uiSetting = useSettingStore()
    const themeName: AppThemeName = uiSetting.is_dark_mode ? 'dark' : 'light'

    updateVuetifyThemeColors('light', { primary })
    updateVuetifyThemeColors('dark', { primary })
    setVuetifyThemeMode(themeName)

    applyDocumentTitle(payload.display_name)
    applyFavicon('favicon_url' in payload ? payload.favicon_url : null)

    if (!hasUserLocalePreference() && payload.default_locale) {
      setLocale(payload.default_locale as Locale)
    }

    if ('sale_settings' in payload) {
      publicSettings.value = payload as SystemSettingPublic
      writeStoredPublicSystemSettings(payload as SystemSettingPublic)
    } else if ('kitchen_enabled' in payload) {
      const nextPublic = toPublicSetting(payload as SystemSetting)
      publicSettings.value = nextPublic
      writeStoredPublicSystemSettings(nextPublic)
    }
  }

  async function fetchPublic() {
    try {
      const response = await http.get<ApiResponseEnvelope<SystemSettingPublic>>(
        buildApiPath(apiEndpoints.systemSetting.public),
      )
      publicSettings.value = response.data
      if (response.data) {
        writeStoredPublicSystemSettings(response.data)
        applyToApp(response.data)
      }
      return response.data
    } catch (exception) {
      const cached = readStoredPublicSystemSettings()
      if (cached) {
        publicSettings.value = cached
        applyToApp(cached)
      }
      throw exception
    }
  }

  async function fetchSettings() {
    isLoading.value = true
    error.value = null

    try {
      const response = await http.get<ApiResponseEnvelope<SystemSetting>>(
        buildApiPath(apiEndpoints.systemSetting.get),
      )
      setting.value = response.data
      bankAccounts.value = response.data?.bank_accounts || []
      form.value = response.data
        ? toSystemSettingForm(response.data)
        : createEmptySystemSettingForm()
      applyToApp(response.data)
      return response.data
    } catch (exception) {
      error.value = getErrorMessage(exception, 'setting.system.errors.loadFailed')
      throw exception
    } finally {
      isLoading.value = false
    }
  }

  async function saveSettings(payload?: SystemSettingForm) {
    isSaving.value = true
    error.value = null

    const body = payload || form.value
    const requestBody = {
      ...body,
      updated_at: body.updated_at || setting.value?.updated_at || null,
      smtp_host: body.smtp_host || null,
      smtp_port: body.smtp_port,
      smtp_encryption: body.smtp_encryption || null,
      smtp_username: body.smtp_username || null,
      smtp_password: body.smtp_password || null,
      smtp_from_email: body.smtp_from_email || null,
      smtp_from_name: body.smtp_from_name || null,
    }

    try {
      const response = await http.put<ApiResponseEnvelope<SystemSetting>, typeof requestBody>(
        buildApiPath(apiEndpoints.systemSetting.update),
        requestBody,
      )
      setting.value = response.data
      bankAccounts.value = response.data?.bank_accounts || bankAccounts.value
      form.value = response.data
        ? { ...toSystemSettingForm(response.data), smtp_password: '' }
        : form.value
      applyToApp(response.data)
      return response.data
    } catch (exception) {
      error.value = getErrorMessage(exception)
      throw exception
    } finally {
      isSaving.value = false
    }
  }

  async function presignBranding(payload: BrandingPresignPayload) {
    const response = await http.post<
      ApiResponseEnvelope<BrandingPresignResponse>,
      BrandingPresignPayload
    >(buildApiPath(apiEndpoints.systemSetting.brandingPresign), payload)

    if (!response.data) {
      throw new Error(translate('setting.system.errors.brandingUploadFailed'))
    }

    return response.data
  }

  async function confirmBranding(type: BrandingAssetType, objectKey: string) {
    const response = await http.post<
      ApiResponseEnvelope<SystemSetting>,
      { type: BrandingAssetType; object_key: string }
    >(buildApiPath(apiEndpoints.systemSetting.brandingConfirm), {
      type,
      object_key: objectKey,
    })

    setting.value = response.data
    if (response.data) {
      if (response.data.updated_at) {
        form.value.updated_at = response.data.updated_at
      }
      bankAccounts.value = response.data.bank_accounts || bankAccounts.value
      applyToApp(response.data)
    }
    return response.data
  }

  async function uploadBranding(type: BrandingAssetType, file: File) {
    isUploadingBranding.value = true
    error.value = null

    try {
      const upload = await presignBranding({
        type,
        filename: file.name,
        mime_type: file.type || 'image/png',
        size: file.size,
      })

      const headers = new Headers(upload.headers || {})
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', file.type || 'application/octet-stream')
      }

      const putResponse = await fetch(upload.upload_url, {
        method: 'PUT',
        headers,
        body: file,
      })

      if (!putResponse.ok) {
        throw new Error(translate('setting.system.errors.brandingUploadFailed'))
      }

      return confirmBranding(type, upload.object_key)
    } catch (exception) {
      error.value = getErrorMessage(exception, 'setting.system.errors.brandingUploadFailed')
      throw exception
    } finally {
      isUploadingBranding.value = false
    }
  }

  async function deleteBranding(type: BrandingAssetType) {
    isUploadingBranding.value = true
    error.value = null

    try {
      const response = await http.delete<ApiResponseEnvelope<SystemSetting>>(
        buildApiPath(apiEndpoints.systemSetting.brandingDelete, {
          params: { type },
        }),
      )
      setting.value = response.data
      if (response.data) {
        if (response.data.updated_at) {
          form.value.updated_at = response.data.updated_at
        }
        bankAccounts.value = response.data.bank_accounts || bankAccounts.value
        applyToApp(response.data)
      }
      return response.data
    } catch (exception) {
      error.value = getErrorMessage(exception, 'setting.system.errors.brandingDeleteFailed')
      throw exception
    } finally {
      isUploadingBranding.value = false
    }
  }

  async function testSmtp(toEmail: string) {
    isTestingSmtp.value = true
    error.value = null

    try {
      await http.post<ApiResponseEnvelope<null>, { to_email: string }>(
        buildApiPath(apiEndpoints.systemSetting.smtpTest),
        { to_email: toEmail },
      )
    } catch (exception) {
      error.value = getErrorMessage(exception, 'setting.system.errors.smtpTestFailed')
      throw exception
    } finally {
      isTestingSmtp.value = false
    }
  }

  async function createBankAccount(payload: BankAccountForm) {
    isSavingBankAccount.value = true
    error.value = null

    try {
      const response = await http.post<ApiResponseEnvelope<BankAccount>, BankAccountForm>(
        buildApiPath(apiEndpoints.systemSetting.bankAccountCreate),
        payload,
      )
      if (response.data) {
        bankAccounts.value = [
          ...bankAccounts.value.filter((item) => item.id !== response.data!.id),
          response.data,
        ]
          .map((item) =>
            response.data!.is_default && item.id !== response.data!.id
              ? { ...item, is_default: false }
              : item,
          )
          .sort(
            (a, b) => Number(b.is_default) - Number(a.is_default) || a.sort_order - b.sort_order,
          )
      }
      return response.data
    } catch (exception) {
      error.value = getErrorMessage(exception)
      throw exception
    } finally {
      isSavingBankAccount.value = false
    }
  }

  async function updateBankAccount(id: number, payload: BankAccountForm) {
    isSavingBankAccount.value = true
    error.value = null

    try {
      const response = await http.put<ApiResponseEnvelope<BankAccount>, BankAccountForm>(
        buildApiPath(apiEndpoints.systemSetting.bankAccountUpdate, {
          params: { id },
        }),
        payload,
      )
      if (response.data) {
        bankAccounts.value = bankAccounts.value
          .map((item) => {
            if (item.id === response.data!.id) return response.data!
            if (response.data!.is_default) return { ...item, is_default: false }
            return item
          })
          .sort(
            (a, b) => Number(b.is_default) - Number(a.is_default) || a.sort_order - b.sort_order,
          )
      }
      return response.data
    } catch (exception) {
      error.value = getErrorMessage(exception)
      throw exception
    } finally {
      isSavingBankAccount.value = false
    }
  }

  async function deleteBankAccount(id: number) {
    isSavingBankAccount.value = true
    error.value = null

    try {
      await http.delete(
        buildApiPath(apiEndpoints.systemSetting.bankAccountDelete, {
          params: { id },
        }),
      )
      bankAccounts.value = bankAccounts.value.filter((item) => item.id !== id)
    } catch (exception) {
      error.value = getErrorMessage(exception)
      throw exception
    } finally {
      isSavingBankAccount.value = false
    }
  }

  return {
    setting,
    publicSettings,
    bankAccounts,
    form,
    isLoading,
    isSaving,
    isUploadingBranding,
    isTestingSmtp,
    isSavingBankAccount,
    error,
    saleSettings,
    displayName,
    logoUrl,
    faviconUrl,
    currentLocale: computed(() => getCurrentLocale()),
    applyToApp,
    fetchPublic,
    fetchSettings,
    saveSettings,
    uploadBranding,
    deleteBranding,
    testSmtp,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
  }
})
